// @fluxora/auth — ROPC (custom-form) auth provider.

//
// The country CRM shows its OWN login form (no redirect to Keycloak's hosted
// page). `login(email, password)` POSTs the credentials to the backend
// (Accounts service `POST /v1/auth/login`), which performs the Keycloak
// password grant and returns an access token (+ a refresh token). We decode
// the token's claims for the UI.
//
// SESSION CONTINUITY (2026-06-19): the session is persisted via
// `@fluxora/storage` (sessionStorage backend — survives a page REFRESH, clears
// on tab close) so a reload no longer logs the user out. Because the Keycloak
// access token is short-lived (~5 min), we also silently renew it against
// `POST /v1/auth/refresh` — both on reload when the stored access token has
// already expired, and proactively on a timer shortly before it expires —
// using the stored refresh token. A refresh that fails (expired / revoked /
// rotated-away token → 401) clears the session and drops the user to /login.
//
// SESSION EXPIRY IS ENFORCED, NOT MERELY RENEWED (2026-09-17): renewal is
// best-effort — a suspended machine freezes its timers, a refresh call can fail
// to reach Accounts, a restored session may carry no refresh token — and every
// one of those used to leave an expired access token in state with
// `isAuthenticated` still saying yes, because it only asked whether a token was
// PRESENT. The CRM then sat there looking signed in while every backend call
// failed. A watchdog now ends the session at `exp` when renewal did not happen,
// re-checks the wall clock whenever the tab wakes, and `isAuthenticated` reads
// the token's validity rather than its existence.
//
// Feeds the SAME shared AuthContext as the OIDC + dummy providers, so
// `useAuth` / `RequireAuth` / `RequireRole` and the api-client bridge are
// provider-agnostic. NO `oidc-client-ts` dependency (unlike ./oidc.tsx).
//
// JWT-authoritative: tenantId / countryCode / roles come only from the token's
// claims, never client input.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { createStorage } from '@fluxora/storage';

import { readCrmClaims, scopesToRoles, type RheoraTokenClaims } from './claims.js';
import { AuthContext } from './context.js';
import { refreshStoredSession, requestRefresh, singleFlight } from './session-refresh.js';
import { isLoginChallenge, sealPassword, type SealedLoginBody } from './login-crypto.js';
import type { AuthContextValue, AuthUser } from './types.js';

export interface RopcConfig {
  /** Full URL of the backend ROPC login endpoint, e.g.
   * `http://localhost:3011/v1/auth/login`. */
  loginUrl: string;
  /**
   * Full URL of the backend session-refresh endpoint. Defaults to `loginUrl`
   * with the trailing `/login` swapped for `/refresh`
   * (`…/v1/auth/login` → `…/v1/auth/refresh`).
   */
  refreshUrl?: string;
  /**
   * TEST KNOB — end the session this many seconds after sign-in, and do not
   * renew it. Omit in any real deployment.
   *
   * ⚠️ WHY IT ALSO DISABLES RENEWAL, WHICH IS THE WHOLE POINT. A tab holding a
   * refresh token does NOT expire: the watchdog renews ~30s before `exp`, so
   * simply shortening the token just makes it renew more often and the
   * operator is never signed out. Expiry is what happens when renewal CANNOT
   * happen — the machine slept, the network went, the refresh token was
   * revoked. This knob reproduces that state in N seconds instead of waiting
   * for one of those: the session is stored WITHOUT its refresh token, so
   * nothing can save it and the watchdog ends it exactly on time.
   *
   * It caps only what the browser believes. The access token itself is
   * untouched and stays valid at the backend for its real lifetime — this
   * exercises the CRM's own expiry path, not the service's.
   *
   * The consuming app is responsible for never passing this in a production
   * build (the US CRM gates it on `import.meta.env.DEV`), which is also why it
   * lives in config rather than being read from the environment here — this
   * package never reads `import.meta.env`.
   */
  testSessionSeconds?: number;
  /**
   * Full URL of the one-time login-challenge endpoint the password is
   * encrypted against. Defaults to `loginUrl` with the trailing `/login`
   * swapped for `/login-challenge` (`…/v1/auth/login` →
   * `…/v1/auth/login-challenge`). See ./login-crypto.ts.
   */
  challengeUrl?: string;
}

export interface RopcAuthProviderProps {
  config: RopcConfig;
  children: ReactNode;
}

type RheoraClaims = RheoraTokenClaims;

/** UTF-8-safe decode of a JWT payload (no signature check — the backend verifies). */
function decodeJwtPayload(token: string): RheoraClaims {
  const part = token.split('.')[1] ?? '';
  const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(b64)
      .split('')
      .map((c) => '%' + `00${c.charCodeAt(0).toString(16)}`.slice(-2))
      .join(''),
  );
  return JSON.parse(json) as RheoraClaims;
}

/**
 * Project the access token's claims onto the provider-agnostic AuthUser. Role
 * mapping and claim reading are the shared ./claims.ts helpers, so this and
 * the OIDC provider cannot drift.
 */
function projectClaims(profile: RheoraClaims): AuthUser {
  const realmRoles = profile.realm_access?.roles ?? [];
  const oauthScopes = (profile.scope ?? '').split(/\s+/).filter(Boolean);
  const scopes = [...new Set([...realmRoles, ...oauthScopes])];
  const tenantId = profile.tenant_id ?? null;
  const { claims, claimsVersion } = readCrmClaims(profile);
  return {
    id: profile.sub ?? '',
    email: profile.email ?? '',
    name: profile.name ?? profile.preferred_username ?? profile.email ?? '',
    roles: scopesToRoles(scopes, tenantId),
    tenantId,
    countryCode: profile.country_code ?? null,
    scopes,
    claims,
    claimsVersion,
    photoUrl: profile.picture,
  };
}

interface LoginResponse {
  accessToken: string;
  tokenType?: string;
  expiresIn?: number;
  refreshToken?: string;
}

/** Persisted session shape (sessionStorage). `exp` is the access-token expiry, epoch seconds. */
interface PersistedSession {
  accessToken: string;
  refreshToken?: string;
  exp: number;
}

// All persistence routes through @fluxora/storage per CLAUDE.md hard rule —
// no direct localStorage/sessionStorage in country code. The `ropc-auth`
// namespace scopes the key away from any other module's state. The storage
// backend is sessionStorage: the session survives a refresh but not a tab
// close (deliberately narrower exposure than localStorage for a bearer token).
const authStorage = createStorage('ropc-auth');
const STORAGE_KEY = 'session';
// Renew this many seconds BEFORE the access token's `exp`, so an in-flight
// request never races a just-expired token.
const REFRESH_SKEW_SECONDS = 30;
// A renewal that never reached the server is retried this often, but only for
// as long as the access token it is renewing is still alive. Past `exp` there
// is nothing left to protect and the session ends.
const REFRESH_RETRY_MS = 5_000;
// The expiry watchdog fires this far PAST `exp`, so it always loses a race with
// the renewal timer (which fires REFRESH_SKEW_SECONDS early) and never ends a
// session a successful renewal was about to replace.
const EXPIRY_GRACE_MS = 1_000;
// `setTimeout` stores its delay in a 32-bit int; anything larger overflows and
// fires immediately. See the `arm` helper in the watchdog.
const MAX_TIMEOUT_MS = 2_147_483_647;
// How many times we will renew an ALREADY-EXPIRED session before concluding the
// answer will not change. One. See `enforceExpiry`.
const MAX_STALE_RENEWALS = 1;
// Longer than the backend's own identity-provider timeout (15s), so a server
// that is slow still gets to answer in its own words. See `login`.
const LOGIN_TIMEOUT_MS = 25_000;

/** A login-step failure whose message is already fit to show the operator. */
class LoginStepError extends Error {
  override name = 'LoginStepError';
}

/**
 * Is this session's access token still inside its own validity window?
 *
 * ⚠️ THE SESSION IS DEAD THE MOMENT THIS IS FALSE, WHETHER OR NOT ANYTHING HAS
 * NOTICED. `isAuthenticated` was `token !== null` — the mere PRESENCE of a
 * token — so an expired one still read as a live session: the CRM kept
 * rendering, `RequireAuth` kept passing, and every backend call failed (401
 * from Accounts / Billing, **403** from Quotation, whose verifier answers
 * `NOT_AUTHORISED` for `"exp" claim timestamp check failed`). The operator was
 * left on a signed-in-looking CRM where nothing loaded. See the watchdog in
 * {@link RopcAuthProvider} for the other half of the fix.
 */
function isSessionLive(session: PersistedSession | null): boolean {
  return session !== null && session.exp > nowSeconds();
}

function readSession(): PersistedSession | null {
  const parsed = authStorage.get<unknown>(STORAGE_KEY);
  if (!parsed || typeof parsed !== 'object') return null;
  const c = parsed as Partial<PersistedSession>;
  if (typeof c.accessToken !== 'string' || c.accessToken.length === 0) return null;
  if (typeof c.exp !== 'number') return null;
  return c as PersistedSession;
}

function writeSession(session: PersistedSession | null): void {
  if (session === null) {
    authStorage.remove(STORAGE_KEY);
    return;
  }
  authStorage.set(STORAGE_KEY, session);
}

/**
 * Why the last session ended, when it ended on its own.
 *
 * ── WHY THIS IS IN STORAGE AND NOT IN ROUTER STATE ────────────────────────
 * The consuming app's route guard puts a reason in the redirect it performs,
 * which covers an expiry the operator was present for. It cannot cover the MOST
 * COMMON real expiry: shut the laptop, come back the next morning, and RELOAD.
 * On a cold load the provider hydrates, finds the stored token long past `exp`,
 * clears it — and the guard never once observed an authenticated state, so it
 * has no idea a session existed and renders a bare login form with no
 * explanation. The evidence lives here, at hydrate time, where the provider can
 * see it and the guard cannot.
 *
 * Written only when a session ends BY ITSELF. A deliberate sign-out clears it,
 * and so does a successful login — so a notice can never outlive the event it
 * describes.
 */
const END_REASON_KEY = 'ended';

export type SessionEndReason = 'expired' | 'rejected';

function markSessionEnded(reason: SessionEndReason): void {
  authStorage.set(END_REASON_KEY, reason);
}

function clearSessionEndedMark(): void {
  authStorage.remove(END_REASON_KEY);
}

/**
 * Why the last session ended on its own, or null when it did not (a deliberate
 * sign-out, a first visit, a session that is still alive). Reading does NOT
 * clear it; the next login does.
 */
export function readSessionEndReason(): SessionEndReason | null {
  const value = authStorage.get<unknown>(END_REASON_KEY);
  return value === 'expired' || value === 'rejected' ? value : null;
}

/**
 * Best-effort projection — a corrupted stored token must not crash render.
 * Exported for tests only (not re-exported from the package entry).
 */
export function projectRopcAccessToken(accessToken: string): AuthUser | null {
  try {
    return projectClaims(decodeJwtPayload(accessToken));
  } catch {
    return null;
  }
}

/** Access-token expiry (epoch seconds) — prefer the JWT `exp`, fall back to `expiresIn`. */
function expiryOf(accessToken: string, expiresIn: number | undefined): number {
  try {
    const exp = decodeJwtPayload(accessToken).exp;
    if (typeof exp === 'number') return exp;
  } catch {
    /* fall through */
  }
  return Math.floor(Date.now() / 1000) + (expiresIn ?? 0);
}

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

/**
 * The `name` of a thrown value, whatever its prototype chain.
 *
 * An aborted `fetch` rejects with a DOMException, and whether that is an
 * `instanceof Error` depends on the runtime — it is not under jsdom. Reading
 * the property directly is the only form that holds in a browser, in Node and
 * in a test environment alike.
 */
function errorName(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'name' in err) {
    return String((err as { name: unknown }).name);
  }
  return '';
}

export function RopcAuthProvider({ config, children }: RopcAuthProviderProps): JSX.Element {
  const [session, setSession] = useState<PersistedSession | null>(null);
  const [isHydrated, setHydrated] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expiryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshInFlight = useRef<Promise<PersistedSession | null> | null>(null);
  /** Renewals spent on an already-expired session. Reset the moment one is live. */
  const staleRenewals = useRef(0);

  const refreshUrl = useMemo(
    () => config.refreshUrl ?? config.loginUrl.replace(/\/login(\/)?$/, '/refresh'),
    [config.refreshUrl, config.loginUrl],
  );
  const logoutUrl = useMemo(
    () => config.loginUrl.replace(/\/login(\/)?$/, '/logout'),
    [config.loginUrl],
  );
  const challengeUrl = useMemo(
    () => config.challengeUrl ?? config.loginUrl.replace(/\/login(\/)?$/, '/login-challenge'),
    [config.challengeUrl, config.loginUrl],
  );

  const user = useMemo(() => (session ? projectRopcAccessToken(session.accessToken) : null), [session]);
  const token = session?.accessToken ?? null;

  // Commit a session to state + storage (single source of truth).
  const commit = useCallback((next: PersistedSession | null): void => {
    setSession(next);
    writeSession(next);
  }, []);

  /**
   * The ONE raw refresh request, single-flight, shared by the watchdog
   * (`refresh` below) and the on-demand `refreshSession`. Keycloak rotates the
   * refresh token on use, so two parallel requests would race and the loser
   * would be rejected. Policy is applied by each caller; see ./session-refresh.ts.
   */
  const requestOnce = useMemo(
    () =>
      singleFlight((refreshToken: string) => requestRefresh(refreshUrl, refreshToken, expiryOf)),
    [refreshUrl],
  );

  // WATCHDOG POLICY. Swap the refresh token for a fresh access token. Returns
  // the new session, or null (and clears) when the refresh token is no longer
  // valid.
  const refresh = useCallback(
    async (refreshToken: string): Promise<PersistedSession | null> => {
      const outcome = await requestOnce(refreshToken);
      if (outcome.kind === 'unreachable') {
        // Network blip, or the refresh token was never judged (5xx) — keep the
        // (possibly still-valid) session; a later call / timer retry can
        // recover. Do NOT clear on transport errors.
        return null;
      }
      if (outcome.kind === 'rejected') {
        // 400/401/403 (or a 2xx with no access token) = the refresh token
        // itself was rejected (expired, revoked, rotated away) → the session
        // is over.
        commit(null);
        markSessionEnded('rejected');
        return null;
      }
      commit(outcome.session);
      return outcome.session;
    },
    [requestOnce, commit],
  );

  /**
   * ON-DEMAND POLICY. Renew now; `true` when a renewed session is in place.
   * Never clears the session and never signs out: a failure is just `false`.
   * Reads the stored session at call time so it cannot renew a stale one.
   */
  const refreshSession = useCallback(
    (): Promise<boolean> =>
      refreshStoredSession({ read: readSession, commit, request: requestOnce }),
    [commit, requestOnce],
  );

  /**
   * Single-flight renewal. The watchdog below can ask for a renewal from three
   * places at once (the scheduled timer, a tab waking up, the expiry sweep);
   * without this they race and the loser commits a session built on a refresh
   * token the winner has already rotated away.
   */
  const renewOnce = useCallback(
    (refreshToken: string): Promise<PersistedSession | null> => {
      if (refreshInFlight.current !== null) return refreshInFlight.current;
      const attempt = refresh(refreshToken).finally(() => {
        refreshInFlight.current = null;
      });
      refreshInFlight.current = attempt;
      return attempt;
    },
    [refresh],
  );

  /**
   * Renewal AND expiry watchdog — two jobs, one effect, deliberately.
   *
   * ── WHY THE SECOND JOB EXISTS ──────────────────────────────────────────────
   * Renewal alone used to be the whole of this effect, and renewal is
   * best-effort by nature: the refresh call can fail to reach the server, the
   * timer is throttled to near-nothing in a background tab and does not run AT
   * ALL while the machine is asleep, and a session restored without a refresh
   * token has nothing to renew with. Every one of those paths ends the same
   * way — an access token past its `exp` still sitting in state, with the CRM
   * still rendering as signed in because `isAuthenticated` only asked whether a
   * token was PRESENT. That is the "session expired but it never logged me out"
   * report: an operator on a live-looking CRM where every screen fails.
   *
   * So the timer that renews is paired with a timer that ENDS the session at
   * `exp` when renewal did not happen, and both are re-evaluated on wake
   * (`visibilitychange` / `focus` / `online`) — a wall-clock check when the tab
   * comes back is the only thing that catches a session that died while the
   * timers were frozen.
   *
   * Clearing the session is all the logout this needs: `isAuthenticated` turns
   * false, and the consuming app's route guard sends the operator to /login.
   */
  useEffect(() => {
    if (session === null) return;
    let cancelled = false;

    const clearTimers = (): void => {
      if (refreshTimer.current !== null) {
        clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
      if (expiryTimer.current !== null) {
        clearTimeout(expiryTimer.current);
        expiryTimer.current = null;
      }
    };

    /** The session is over. Dropping it is what signs the operator out. */
    const endSession = (): void => {
      if (cancelled) return;
      commit(null);
      // So /login can still say why, even if the operator only finds out on a
      // reload hours later. See `readSessionEndReason`.
      markSessionEnded('expired');
    };

    const renew = (): void => {
      const refreshToken = session.refreshToken;
      if (refreshToken === undefined) return;
      void renewOnce(refreshToken).then((next) => {
        // A success re-runs this whole effect with the new session; nothing to
        // schedule here. A null means one of two things, and only one of them
        // is worth retrying: `refresh` CLEARED the session (the token was
        // rejected — `readSession()` is now empty, leave it alone), or the
        // request never reached the server. Retry the second, and only while
        // the access token we are renewing is still alive.
        if (cancelled || next !== null) return;
        if (readSession() !== null && nowSeconds() < session.exp) {
          refreshTimer.current = setTimeout(renew, REFRESH_RETRY_MS);
        }
      });
    };

    /**
     * The access token has expired (or the tab woke up to find it expired).
     * One last renewal attempt — after sleep the timers never fired but the
     * refresh token is often still good — and failing that, sign out.
     *
     * ⚠️ THE ATTEMPT BUDGET IS LOAD-BEARING, NOT DEFENSIVE. Renewing an expired
     * session commits a new one, which re-runs this effect, which schedules
     * again — so if the renewal comes back with a token that is ALSO not live,
     * that is a closed loop with no delay in it. Measured before the budget
     * existed: **2,389 `POST /v1/auth/refresh` in 10 seconds** from a single
     * tab, ~6-9ms apart, never stopping, still running while the operator sat
     * on /login. The trigger is ordinary clock skew — a workstation whose clock
     * is further ahead of the IdP's than the token's own 5-minute lifetime, so
     * every freshly minted token reads as already expired here.
     *
     * One attempt per expired-session episode. If that attempt does not produce
     * a LIVE session, the session is over — which is the honest answer for a
     * skewed clock too, since nothing the server mints will ever look valid.
     */
    const enforceExpiry = (): void => {
      if (cancelled) return;
      const settle = (next: PersistedSession | null): void => {
        if (next === null || !isSessionLive(next)) endSession();
      };
      const inFlight = refreshInFlight.current;
      if (inFlight !== null) {
        void inFlight.then(settle);
        return;
      }
      const refreshToken = session.refreshToken;
      if (refreshToken === undefined || staleRenewals.current >= MAX_STALE_RENEWALS) {
        endSession();
        return;
      }
      staleRenewals.current += 1;
      void renewOnce(refreshToken).then(settle);
    };

    const schedule = (): void => {
      clearTimers();
      if (!isSessionLive(session)) {
        enforceExpiry();
        return;
      }
      // A live session is proof the budget is not needed any more.
      staleRenewals.current = 0;
      const msUntilExpiry = (session.exp - nowSeconds()) * 1000;
      /**
       * ⚠️ `setTimeout` TAKES A 32-BIT DELAY. Anything past 2^31-1 ms (~24.85
       * days) overflows and fires IMMEDIATELY — so a long-lived token used to
       * be signed out the instant it was restored, the one case where the
       * session was unarguably fine. Past the ceiling we re-check instead of
       * acting, which also re-reads the wall clock, which is what we want after
       * a wait that long.
       */
      const arm = (
        ref: typeof refreshTimer,
        delayMs: number,
        fire: () => void,
      ): void => {
        const ms = Math.max(delayMs, 0);
        ref.current =
          ms > MAX_TIMEOUT_MS ? setTimeout(schedule, MAX_TIMEOUT_MS) : setTimeout(fire, ms);
      };
      arm(refreshTimer, msUntilExpiry - REFRESH_SKEW_SECONDS * 1000, renew);
      arm(expiryTimer, msUntilExpiry + EXPIRY_GRACE_MS, enforceExpiry);
    };

    /** Re-read the wall clock — the tab may have been frozen for hours. */
    const revalidate = (): void => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      schedule();
    };

    schedule();

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', revalidate);
      window.addEventListener('online', revalidate);
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', revalidate);
    }

    return () => {
      cancelled = true;
      clearTimers();
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', revalidate);
        window.removeEventListener('online', revalidate);
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', revalidate);
      }
    };
  }, [session, commit, renewOnce]);

  // Hydrate once on mount: restore the persisted session, silently refreshing
  // if the stored access token has already expired.
  useEffect(() => {
    let cancelled = false;
    const stored = readSession();
    if (stored === null) {
      setHydrated(true);
      return;
    }
    /**
     * ⚠️ THE TEST IS `exp`, NOT `exp - REFRESH_SKEW_SECONDS`. It used to be the
     * latter, which threw away a session with 29 seconds of life left and
     * bounced the operator to /login — the one case where the stored token was
     * still unarguably usable. The skew belongs to the RENEWAL schedule, not to
     * the question "is this a session". A live-but-nearly-expired session is
     * adopted here and the watchdog's renewal timer, already past due, fires
     * immediately.
     */
    if (isSessionLive(stored)) {
      setSession(stored);
      setHydrated(true);
      return;
    }
    // Past `exp`. The refresh token is the only thing that can save it.
    if (stored.refreshToken !== undefined) {
      void refresh(stored.refreshToken)
        .then((next) => {
          // `refresh` clears the session itself when the token is REJECTED, but
          // a refresh that never reached the server returns null with the dead
          // session still in storage — where the next page load would find it
          // and try again forever. The access token is already expired, so
          // there is nothing here to preserve.
          if (next === null) {
            commit(null);
            markSessionEnded('expired');
          }
        })
        .finally(() => {
          if (!cancelled) setHydrated(true);
        });
    } else {
      commit(null);
      markSessionEnded('expired');
      setHydrated(true);
    }
    return () => {
      cancelled = true;
    };
    // Run exactly once on mount — refresh/commit are stable (deps are config
    // strings). Re-running on their identity would re-hydrate spuriously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      setLoading(true);
      try {
        // ONE budget for both hops (challenge + login), so the operator's worst
        // case is still LOGIN_TIMEOUT_MS, not twice it.
        const signal = AbortSignal.timeout(LOGIN_TIMEOUT_MS);
        // The password never goes on the wire as plaintext: fetch a one-time
        // challenge, encrypt to the backend's key, send only the ciphertext.
        // See ./login-crypto.ts. No plaintext fallback — any failure here
        // fails the login rather than quietly downgrading it.
        let sealed: SealedLoginBody;
        try {
          const challengeRes = await fetch(challengeUrl, { method: 'GET', cache: 'no-store', signal });
          if (!challengeRes.ok) {
            throw new LoginStepError(
              challengeRes.status === 429
                ? 'Too many sign-in attempts. Please wait a minute and try again.'
                : 'Login is temporarily unavailable.',
            );
          }
          const challenge: unknown = await challengeRes.json();
          if (!isLoginChallenge(challenge)) throw new LoginStepError('Login is temporarily unavailable.');
          sealed = await sealPassword(challenge, email, password);
        } catch (err: unknown) {
          if (err instanceof LoginStepError) throw new Error(err.message);
          const name = errorName(err);
          if (name === 'TimeoutError' || name === 'AbortError') {
            throw new Error(
              'Signing in is taking longer than usual. The identity service may be waking up — please try again.',
            );
          }
          // sealPassword's own "cannot sign in securely" message is worth showing.
          if (err instanceof Error && err.message.startsWith('This browser')) throw err;
          throw new Error('Could not reach the login service. Please try again.');
        }
        let res: Response;
        try {
          res = await fetch(config.loginUrl, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(sealed),
            /**
             * ⚠️ THIS USED TO BE UNBOUNDED, AND SO DID THE HOP BEHIND IT.
             * Keycloak idles out, and the quiet window that lets it idle is the
             * same one that expires a session — so the first login after a
             * timeout logout is the one most likely to meet a cold identity
             * provider. With no bound anywhere the button simply spun, with
             * nothing to read and no end.
             *
             * Longer than the backend's own grant timeout on purpose: when the
             * server is the slow part we want ITS answer (a 503 we can explain),
             * not our own guess. This fires only when even that never arrives.
             */
            signal,
          });
        } catch (err: unknown) {
          // ⚠️ NOT `err instanceof Error`. An aborted fetch rejects with a
          // DOMException, which does not reliably sit on Error's prototype
          // chain across runtimes — under jsdom it does not, so the instanceof
          // form silently misreported every timeout as "could not reach the
          // login service", which is a different problem with a different fix.
          const name = errorName(err);
          if (name === 'TimeoutError' || name === 'AbortError') {
            throw new Error(
              'Signing in is taking longer than usual. The identity service may be waking up — please try again.',
            );
          }
          throw new Error('Could not reach the login service. Please try again.');
        }
        if (!res.ok) {
          if (res.status === 401) throw new Error('Invalid email or password.');
          if (res.status === 503) throw new Error('Login is temporarily unavailable.');
          throw new Error(`Login failed (${res.status}).`);
        }
        const data = (await res.json()) as LoginResponse;
        if (typeof data.accessToken !== 'string' || data.accessToken.length === 0) {
          throw new Error('Login response was malformed.');
        }
        const testSeconds = config.testSessionSeconds;
        commit(
          typeof testSeconds === 'number' && testSeconds > 0
            ? // TEST KNOB (see RopcConfig.testSessionSeconds): a short, terminal
              // session. The refresh token is deliberately dropped — a session
              // that can renew never expires, which is the opposite of what
              // this is for.
              { accessToken: data.accessToken, exp: nowSeconds() + testSeconds }
            : {
                accessToken: data.accessToken,
                ...(typeof data.refreshToken === 'string'
                  ? { refreshToken: data.refreshToken }
                  : {}),
                exp: expiryOf(data.accessToken, data.expiresIn),
              },
        );
        // A new session outlives any explanation of the old one.
        clearSessionEndedMark();
      } finally {
        setLoading(false);
      }
    },
    [config.loginUrl, challengeUrl, commit],
  );

  const logout = useCallback(async (): Promise<void> => {
    // Notify the backend so it records a `crm.logout` audit row, while the
    // token is still valid. Best-effort: logout proceeds regardless (network
    // error, expired token, etc.). We read the token from storage rather than
    // state to avoid a stale closure.
    const current = readSession();
    if (current?.accessToken !== undefined && current.accessToken.length > 0) {
      try {
        await fetch(logoutUrl, {
          method: 'POST',
          headers: { authorization: `Bearer ${current.accessToken}` },
        });
      } catch {
        /* best-effort — never block sign-out on the audit call */
      }
    }
    commit(null);
  }, [logoutUrl, commit]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state: user,
      user,
      // An EXPIRED token is not a session. See `isSessionLive` for what this
      // used to be (`token !== null`) and what that cost. The watchdog above
      // clears an expired session within EXPIRY_GRACE_MS, so this guards only
      // the instant between `exp` and that sweep — but it is the instant a
      // route guard would otherwise wave through.
      isAuthenticated: token !== null && isSessionLive(session),
      isHydrated,
      isLoading,
      login,
      logout,
      token,
      refreshSession,
    }),
    [user, token, session, isHydrated, isLoading, login, logout, refreshSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
